import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { customersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
    UserSquare, Plus, Search, Edit2, Trash2, Loader2, ShieldAlert, AlertTriangle, CheckCircle2, Download,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { CountrySelect } from '../components/shared/CountrySelect';
import { AppPhoneInput } from '../components/shared/PhoneInput';
import {
    VAT_PROFILES,
    VAT_PROFILE_KEYS,
    resolveVatRate,
    DEFAULT_VAT_PROFILE_KEY,
    DEFAULT_VAT_RATE,
} from '../lib/vatProfiles';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const emptyAddress = {
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'FR',  // ISO 3166-1 alpha-2 — France by default
};

const emptyCustomer = {
    customer_code: '',
    company_name: '',
    contact_person: '',
    mobile_number: '',
    landline: '',
    email_address: '',
    siret: '',
    tva: '',
    // Tax profile — new customers default to France Standard 20%.
    vat_type: DEFAULT_VAT_PROFILE_KEY,
    default_vat_rate: DEFAULT_VAT_RATE,
    notes: '',
    credit_limit: '',
    overdue_threshold_days: 30,
    billing_address: { ...emptyAddress },
    shipping_same_as_billing: true,
    shipping_address: { ...emptyAddress },
    document_language: '',  // '' = default (French)
};

const fmtEUR = (v) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0);

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// SIRET = 14 digits, optionally grouped 3-3-3-5 for readability
const formatSiret = (raw) => {
    const d = (raw || '').replace(/\D/g, '').slice(0, 14);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
    return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 9)} ${d.slice(9)}`;
};

const formatTva = (raw) => (raw || '').toUpperCase().replace(/\s+/g, '').slice(0, 13);

const Customers = () => {
    const { t } = useTranslation(['entities', 'common']);
    const { isAdmin } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyCustomer);
    const [saving, setSaving] = useState(false);

    // Duplicate detection dialog state
    const [dupOpen, setDupOpen] = useState(false);
    const [dupMatches, setDupMatches] = useState([]);
    const [pendingPayload, setPendingPayload] = useState(null);

    const fetchCustomers = async () => {
        setLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            const { data } = await customersAPI.getAll(params);
            setCustomers(data);
        } catch {
            toast.error(t('common:toasts.load_failed'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomers();
    }, []);

    useEffect(() => {
        const t = setTimeout(fetchCustomers, 300);
        return () => clearTimeout(t);
    }, [search]);

    const openCreate = async () => {
        setEditingId(null);
        let nextCode = '';
        try {
            const { data } = await customersAPI.nextCode();
            nextCode = data?.next || '';
        } catch {
            /* non-blocking */
        }
        setForm({ ...emptyCustomer, customer_code: nextCode });
        setOpen(true);
    };

    const openEdit = (c) => {
        setEditingId(c.id);
        setForm({
            ...emptyCustomer,
            ...c,
            customer_code: c.customer_code || '',
            credit_limit: c.credit_limit ?? '',
            overdue_threshold_days: c.overdue_threshold_days ?? 30,
            billing_address: { ...emptyAddress, ...(c.billing_address || {}) },
            shipping_same_as_billing: c.shipping_same_as_billing ?? true,
            shipping_address: { ...emptyAddress, ...(c.shipping_address || {}) },
            document_language: c.document_language || '',
        });
        setOpen(true);
    };

    const emailValid = useMemo(() => {
        if (!form.email_address) return null;
        return EMAIL_RE.test(form.email_address.trim());
    }, [form.email_address]);

    const buildPayload = (confirmDuplicates = false) => {
        const billing = form.billing_address || emptyAddress;
        const shipping = form.shipping_same_as_billing
            ? billing
            : form.shipping_address || emptyAddress;
        return {
            customer_code: form.customer_code?.trim() || null,
            company_name: form.company_name.trim(),
            contact_person: (form.contact_person || '').trim(),
            mobile_number: (form.mobile_number || '').trim(),
            landline: (form.landline || '').trim(),
            email_address: (form.email_address || '').trim(),
            siret: (form.siret || '').replace(/\D/g, ''),
            tva: (form.tva || '').replace(/\s+/g, '').toUpperCase(),
            vat_type: form.vat_type || DEFAULT_VAT_PROFILE_KEY,
            default_vat_rate:
                form.default_vat_rate === '' || form.default_vat_rate == null
                    ? DEFAULT_VAT_RATE
                    : parseFloat(form.default_vat_rate),
            notes: (form.notes || '').trim(),
            credit_limit:
                form.credit_limit === '' || form.credit_limit === null
                    ? null
                    : parseFloat(form.credit_limit),
            overdue_threshold_days:
                form.overdue_threshold_days === '' || form.overdue_threshold_days === null
                    ? 30
                    : parseInt(form.overdue_threshold_days, 10),
            billing_address: billing,
            shipping_same_as_billing: !!form.shipping_same_as_billing,
            shipping_address: shipping,
            document_language: form.document_language || null,
            confirm_duplicates: confirmDuplicates,
        };
    };

    const submit = async (payload) => {
        if (editingId) {
            await customersAPI.update(editingId, payload);
            toast.success(t('entities:customers.toasts.updated'));
        } else {
            await customersAPI.create(payload);
            toast.success(t('entities:customers.toasts.created'));
        }
        setOpen(false);
        fetchCustomers();
    };

    const handleSave = async () => {
        if (!form.company_name?.trim()) {
            toast.error(t('entities:customers.dialog.company_required'));
            return;
        }
        if (form.email_address && !EMAIL_RE.test(form.email_address.trim())) {
            toast.error('Invalid email address');
            return;
        }
        setSaving(true);
        const payload = buildPayload(false);
        try {
            await submit(payload);
        } catch (e) {
            const status = e.response?.status;
            const detail = e.response?.data?.detail;
            if (status === 409 && detail && Array.isArray(detail.duplicates)) {
                setDupMatches(detail.duplicates);
                setPendingPayload(payload);
                setDupOpen(true);
            } else {
                const msg =
                    typeof detail === 'string'
                        ? detail
                        : detail?.message || 'Save failed';
                toast.error(msg);
            }
        } finally {
            setSaving(false);
        }
    };

    const confirmDuplicateSave = async () => {
        if (!pendingPayload) return;
        setSaving(true);
        // Drop the originally auto-fetched customer_code so the backend allocates
        // a fresh one — otherwise the prior save will have consumed UC-CSTX### and
        // we'd hit "Customer code already used" (400).
        const retryPayload = {
            ...pendingPayload,
            customer_code: editingId ? pendingPayload.customer_code : null,
            confirm_duplicates: true,
        };
        try {
            await submit(retryPayload);
            setDupOpen(false);
            setDupMatches([]);
            setPendingPayload(null);
        } catch (e) {
            const detail = e.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail : detail?.message || 'Save failed';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this customer?')) return;
        try {
            await customersAPI.delete(id);
            toast.success(t('entities:customers.toasts.deleted'));
            fetchCustomers();
        } catch {
            toast.error('Delete failed');
        }
    };

    // ---------- CSV export ----------
    const escCsv = (v) => {
        if (v === null || v === undefined) return '';
        const s = String(v);
        return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const fmtAddr = (a) =>
        !a
            ? ''
            : [a.line1, a.line2, a.postal_code, a.city, a.state, a.country]
                  .filter(Boolean)
                  .join(', ');

    const exportCSV = () => {
        if (!customers.length) {
            toast.error('No customers to export');
            return;
        }
        const headers = [
            'Customer Code', 'Company Name', 'Contact Person',
            'Email', 'Mobile', 'Landline',
            'SIRET', 'TVA',
            'Billing Address', 'Postal Code', 'City', 'Country',
            'Shipping Address',
            'Credit Limit (EUR)', 'Overdue Threshold (days)',
            'Outstanding (EUR)', 'Overdue (EUR)',
            'Notes', 'Created At',
        ];
        const rows = customers.map((c) => [
            c.customer_code,
            c.company_name,
            c.contact_person,
            c.email_address,
            c.mobile_number,
            c.landline,
            c.siret,
            c.tva,
            fmtAddr(c.billing_address),
            c.billing_address?.postal_code,
            c.billing_address?.city,
            c.billing_address?.country,
            c.shipping_same_as_billing ? '(same as billing)' : fmtAddr(c.shipping_address),
            c.credit_limit ?? '',
            c.overdue_threshold_days ?? '',
            c.outstanding_amount ?? 0,
            c.overdue_amount ?? 0,
            c.notes,
            c.created_at,
        ]);
        const csv = [headers, ...rows]
            .map((r) => r.map(escCsv).join(','))
            .join('\r\n');
        // UTF-8 BOM so Excel opens accented characters correctly
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const ts = new Date().toISOString().slice(0, 10);
        a.href = url;
        a.download = `customers_${ts}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`Exported ${customers.length} customer${customers.length > 1 ? 's' : ''}`);
    };

    // ---------- Address helpers ----------
    const setBilling = (patch) =>
        setForm((f) => ({ ...f, billing_address: { ...f.billing_address, ...patch } }));
    const setShipping = (patch) =>
        setForm((f) => ({ ...f, shipping_address: { ...f.shipping_address, ...patch } }));

    const onPickBillingSuggestion = (s) => {
        setBilling({
            line1: s.line1 || '',
            city: s.city || '',
            postal_code: s.postal_code || '',
            state: s.state || '',
            country: s.country || 'France',
        });
    };
    const onPickShippingSuggestion = (s) => {
        setShipping({
            line1: s.line1 || '',
            city: s.city || '',
            postal_code: s.postal_code || '',
            state: s.state || '',
            country: s.country || 'France',
        });
    };

    return (
        <div data-testid="customers-page" className="space-y-8 animate-fadeIn">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="text-[11px] uppercase tracking-[2px] text-[#C08478] font-semibold mb-2">
                        {t('entities:customers.section_label')}
                    </p>
                    <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">{t('entities:customers.title')}</h1>
                    <p className="text-gray-500 mt-1">
                        {t('entities:customers.subtitle')}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={exportCSV}
                        variant="outline"
                        data-testid="export-customers-csv"
                        disabled={customers.length === 0}
                        className="border-[#E5E5E5] text-[#1A1A1A] hover:bg-[#F5F5F5]"
                    >
                        <Download size={16} className="mr-2" /> Export CSV
                    </Button>
                    <Button
                        onClick={openCreate}
                        data-testid="new-customer-btn"
                        className="bg-[#1A1A1A] hover:bg-[#C08478] text-white"
                    >
                        <Plus size={16} className="mr-2" /> {t('entities:customers.add')}
                    </Button>
                </div>
            </div>

            <Card className="border-0 shadow-sm">
                <CardHeader className="flex flex-row justify-between items-center">
                    <CardTitle className="flex items-center gap-2">
                        <UserSquare className="text-[#C08478]" size={20} />
                        {t('entities:customers.all')} ({customers.length})
                    </CardTitle>
                    <div className="relative w-full max-w-xs">
                        <Search
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        <Input
                            data-testid="customer-search"
                            placeholder={t('entities:customers.search_placeholder')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9 bg-[#F5F5F5] border-[#E5E5E5]"
                        />
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="py-16 flex justify-center">
                            <Loader2 className="animate-spin text-[#C08478]" />
                        </div>
                    ) : customers.length === 0 ? (
                        <div className="py-16 text-center text-gray-400">
                            {t('entities:customers.empty')}
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('entities:customers.columns.code')}</TableHead>
                                    <TableHead>{t('entities:customers.columns.company')}</TableHead>
                                    <TableHead>{t('entities:customers.columns.contact')}</TableHead>
                                    <TableHead>{t('entities:customers.columns.mobile')}</TableHead>
                                    <TableHead className="text-right">{t('entities:customers.columns.outstanding')}</TableHead>
                                    <TableHead className="text-right">{t('entities:customers.columns.credit_limit')}</TableHead>
                                    <TableHead>{t('entities:customers.columns.status')}</TableHead>
                                    <TableHead className="text-right">{t('common:common.actions')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {customers.map((c) => {
                                    const outstanding = c.outstanding_amount || 0;
                                    const overdue = c.overdue_amount || 0;
                                    const limit = c.credit_limit;
                                    const overLimit = c.over_credit_limit;
                                    const usagePct =
                                        limit && limit > 0
                                            ? Math.min(100, (outstanding / limit) * 100)
                                            : 0;
                                    return (
                                        <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                                            <TableCell
                                                className="font-mono text-xs text-[#C08478]"
                                                data-testid={`customer-code-${c.id}`}
                                            >
                                                {c.customer_code || '-'}
                                            </TableCell>
                                            <TableCell>
                                                <p className="font-medium text-[#1A1A1A]">
                                                    {c.company_name}
                                                </p>
                                                {c.email_address && (
                                                    <p className="text-xs text-gray-500">
                                                        {c.email_address}
                                                    </p>
                                                )}
                                                {c.siret && (
                                                    <p className="text-[10px] text-gray-400 font-mono">
                                                        SIRET {c.siret}
                                                    </p>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {c.contact_person || '-'}
                                            </TableCell>
                                            <TableCell className="font-mono text-sm">
                                                {c.mobile_number || '-'}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <span
                                                    className={`font-mono font-semibold ${
                                                        outstanding > 0
                                                            ? 'text-[#1A1A1A]'
                                                            : 'text-gray-400'
                                                    }`}
                                                    data-testid={`outstanding-${c.id}`}
                                                >
                                                    {fmtEUR(outstanding)}
                                                </span>
                                                {overdue > 0 && (
                                                    <p className="text-[10px] text-[#E76F51] font-medium mt-0.5">
                                                        {fmtEUR(overdue)} overdue
                                                    </p>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {limit != null ? (
                                                    <div className="space-y-1">
                                                        <span className="font-mono text-sm text-gray-600">
                                                            {fmtEUR(limit)}
                                                        </span>
                                                        {limit > 0 && (
                                                            <div className="w-24 h-1.5 ml-auto bg-[#F5F5F5] rounded-full overflow-hidden">
                                                                <div
                                                                    className={`h-full rounded-full transition-all ${
                                                                        usagePct >= 100
                                                                            ? 'bg-[#E76F51]'
                                                                            : usagePct >= 80
                                                                            ? 'bg-[#E9C46A]'
                                                                            : 'bg-[#2A9D8F]'
                                                                    }`}
                                                                    style={{ width: `${usagePct}%` }}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-400 italic">
                                                        {t('entities:customers.statuses.unlimited')}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {overLimit ? (
                                                    <Badge
                                                        className="bg-[#E76F51] text-white text-[10px]"
                                                        data-testid={`over-limit-badge-${c.id}`}
                                                    >
                                                        <ShieldAlert size={10} className="mr-1" />
                                                        {t('entities:customers.statuses.over_limit')}
                                                    </Badge>
                                                ) : overdue > 0 ? (
                                                    <Badge className="bg-[#E9C46A]/30 text-[#8a6d00] text-[10px]">
                                                        {t('entities:customers.statuses.overdue')}
                                                    </Badge>
                                                ) : outstanding > 0 ? (
                                                    <Badge className="bg-[#264653]/20 text-[#264653] text-[10px]">
                                                        {t('entities:customers.statuses.outstanding')}
                                                    </Badge>
                                                ) : (
                                                    <Badge className="bg-[#2A9D8F]/20 text-[#2A9D8F] text-[10px]">
                                                        {t('entities:customers.statuses.clear')}
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => openEdit(c)}
                                                    data-testid={`edit-customer-${c.id}`}
                                                    className="text-gray-500 hover:text-[#C08478]"
                                                >
                                                    <Edit2 size={16} />
                                                </Button>
                                                {isAdmin() && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleDelete(c.id)}
                                                        data-testid={`delete-customer-${c.id}`}
                                                        className="text-gray-500 hover:text-red-500"
                                                    >
                                                        <Trash2 size={16} />
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* ---------- Create / Edit dialog ---------- */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    className="max-w-3xl max-h-[90vh] overflow-y-auto"
                    data-testid="customer-dialog"
                >
                    <DialogHeader>
                        <DialogTitle>
                            {editingId ? t('entities:customers.dialog.edit') : t('entities:customers.dialog.new')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('entities:customers.dialog.description')}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-0">
                    {/* Identification */}
                    <SectionLabel>{t('entities:customers.dialog.section_identification')}</SectionLabel>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Customer Code</Label>
                            <Input
                                data-testid="customer-code-input"
                                value={form.customer_code}
                                onChange={(e) =>
                                    setForm({ ...form, customer_code: e.target.value })
                                }
                                placeholder="UC-CSTX001"
                                className="font-mono"
                            />
                            <p className="text-[10px] text-gray-400">
                                Auto-generated. Override if needed.
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label>Company Name *</Label>
                            <Input
                                data-testid="customer-company-name"
                                value={form.company_name}
                                onChange={(e) =>
                                    setForm({ ...form, company_name: e.target.value })
                                }
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Contact Person</Label>
                            <Input
                                data-testid="customer-contact-person"
                                value={form.contact_person}
                                onChange={(e) =>
                                    setForm({ ...form, contact_person: e.target.value })
                                }
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <div className="relative">
                                <Input
                                    type="email"
                                    data-testid="customer-email"
                                    value={form.email_address}
                                    onChange={(e) =>
                                        setForm({ ...form, email_address: e.target.value })
                                    }
                                    className={
                                        emailValid === false
                                            ? 'border-[#E76F51] pr-9'
                                            : emailValid === true
                                            ? 'border-[#2A9D8F] pr-9'
                                            : ''
                                    }
                                />
                                {emailValid === true && (
                                    <CheckCircle2
                                        size={14}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2A9D8F]"
                                    />
                                )}
                                {emailValid === false && (
                                    <AlertTriangle
                                        size={14}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#E76F51]"
                                    />
                                )}
                            </div>
                            {emailValid === false && (
                                <p className="text-[10px] text-[#E76F51]">Invalid email format</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label>Mobile</Label>
                            <AppPhoneInput
                                testid="customer-mobile"
                                value={form.mobile_number}
                                onChange={(v) => setForm({ ...form, mobile_number: v })}
                                defaultCountry="FR"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Landline</Label>
                            <AppPhoneInput
                                testid="customer-landline"
                                value={form.landline}
                                onChange={(v) => setForm({ ...form, landline: v })}
                                defaultCountry="FR"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>SIRET N°</Label>
                            <Input
                                data-testid="customer-siret"
                                value={formatSiret(form.siret)}
                                onChange={(e) =>
                                    setForm({ ...form, siret: e.target.value.replace(/\D/g, '') })
                                }
                                placeholder="123 456 789 00012"
                                className="font-mono"
                            />
                            <p className="text-[10px] text-gray-400">
                                14 digits with Luhn checksum
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label>TVA N°</Label>
                            <Input
                                data-testid="customer-tva"
                                value={form.tva}
                                onChange={(e) =>
                                    setForm({ ...form, tva: formatTva(e.target.value) })
                                }
                                placeholder="FR12345678901"
                                className="font-mono"
                            />
                        </div>
                    </div>

                    {/* Tax profile — pre-fills invoice VAT on selection */}
                    <SectionLabel>Tax / VAT</SectionLabel>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>VAT Type</Label>
                            <Select
                                value={form.vat_type || DEFAULT_VAT_PROFILE_KEY}
                                onValueChange={(next) =>
                                    setForm((f) => {
                                        const nextRate = resolveVatRate(next, f.default_vat_rate);
                                        return {
                                            ...f,
                                            vat_type: next,
                                            // For non-custom profiles, snap the default rate to
                                            // the profile's canonical value. Keeps rate + type
                                            // consistent unless the operator explicitly picks CUSTOM.
                                            default_vat_rate: next === VAT_PROFILE_KEYS.CUSTOM
                                                ? f.default_vat_rate
                                                : nextRate,
                                        };
                                    })
                                }
                            >
                                <SelectTrigger data-testid="customer-vat-type">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {VAT_PROFILES.map((p) => (
                                        <SelectItem
                                            key={p.key}
                                            value={p.key}
                                            data-testid={`customer-vat-type-${p.key}`}
                                        >
                                            <span className="flex items-baseline gap-2">
                                                <span>{p.label}</span>
                                                <span className="text-xs text-gray-400 font-mono">
                                                    {p.rate != null ? `${p.rate}%` : '—'}
                                                </span>
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-[10px] text-gray-400">
                                Pre-fills the VAT rate on new invoices for this customer.
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label>Default VAT Rate (%)</Label>
                            <Input
                                data-testid="customer-default-vat-rate"
                                type="number"
                                step="0.1"
                                min="0"
                                max="30"
                                value={form.default_vat_rate ?? ''}
                                disabled={form.vat_type !== VAT_PROFILE_KEYS.CUSTOM}
                                onChange={(e) =>
                                    setForm({ ...form, default_vat_rate: e.target.value })
                                }
                                className="font-mono"
                            />
                            <p className="text-[10px] text-gray-400">
                                {form.vat_type === VAT_PROFILE_KEYS.CUSTOM
                                    ? 'Enter the exact rate for this customer.'
                                    : 'Auto-set from the VAT Type — pick "Custom" to override.'}
                            </p>
                        </div>
                    </div>

                    {/* Billing address */}
                    <SectionLabel>{t('entities:customers.dialog.section_billing')}</SectionLabel>
                    <AddressFields
                        prefix="billing"
                        value={form.billing_address}
                        onChange={setBilling}
                        onPickSuggestion={onPickBillingSuggestion}
                    />

                    {/* Shipping address */}
                    <div className="md:col-span-2 mt-4 pt-4 border-t border-[#F5F5F5] flex items-center justify-between">
                        <div>
                            <p className="text-[10px] uppercase tracking-[2px] text-[#C08478] font-semibold">
                                {t('entities:customers.dialog.section_shipping')}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                                {t('entities:customers.dialog.shipping_hint')}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Label
                                htmlFor="shipping-same-toggle"
                                className="text-xs text-gray-500"
                            >
                                {t('entities:customers.dialog.shipping_same')}
                            </Label>
                            <Switch
                                id="shipping-same-toggle"
                                data-testid="shipping-same-toggle"
                                checked={!!form.shipping_same_as_billing}
                                onCheckedChange={(checked) =>
                                    setForm((f) => ({
                                        ...f,
                                        shipping_same_as_billing: checked,
                                        shipping_address: checked
                                            ? f.billing_address
                                            : f.shipping_address?.line1
                                            ? f.shipping_address
                                            : { ...emptyAddress },
                                    }))
                                }
                            />
                        </div>
                    </div>
                    {!form.shipping_same_as_billing && (
                        <AddressFields
                            prefix="shipping"
                            value={form.shipping_address}
                            onChange={setShipping}
                            onPickSuggestion={onPickShippingSuggestion}
                        />
                    )}

                    {/* Notes + Credit */}
                    <SectionLabel>{t('entities:customers.dialog.section_notes_credit')}</SectionLabel>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2 md:col-span-2">
                            <Label>Notes</Label>
                            <Textarea
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                rows={2}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>
                                Credit Limit (€)
                                <span className="text-xs text-gray-400 ml-2 font-normal">
                                    empty = unlimited
                                </span>
                            </Label>
                            <Input
                                data-testid="customer-credit-limit"
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="No limit"
                                value={form.credit_limit ?? ''}
                                onChange={(e) =>
                                    setForm({ ...form, credit_limit: e.target.value })
                                }
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Overdue Threshold (days)</Label>
                            <Input
                                data-testid="customer-overdue-threshold"
                                type="number"
                                step="1"
                                min="1"
                                value={form.overdue_threshold_days ?? 30}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        overdue_threshold_days: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label>
                                Document Language
                                <span className="text-xs text-gray-400 ml-2 font-normal">
                                    used for invoice / quotation PDFs
                                </span>
                            </Label>
                            <select
                                data-testid="customer-document-language"
                                value={form.document_language || ''}
                                onChange={(e) => setForm({ ...form, document_language: e.target.value })}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            >
                                <option value="">Default — French (FR)</option>
                                <option value="fr">Français (FR)</option>
                                <option value="en">English (EN)</option>
                            </select>
                            <p className="text-[10px] text-gray-400">
                                Leave on default unless this customer should receive English PDFs (e.g. UK/Irish buyers).
                            </p>
                        </div>
                    </div>

                    <DialogFooter className="mt-6">
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            data-testid="save-customer"
                            className="bg-[#1A1A1A] hover:bg-[#C08478] text-white"
                        >
                            {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
                            {editingId ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ---------- Duplicate confirm dialog ---------- */}
            <AlertDialog open={dupOpen} onOpenChange={setDupOpen}>
                <AlertDialogContent data-testid="duplicate-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle size={18} className="text-[#E76F51]" />
                            Possible Duplicate Customer
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            We found {dupMatches.length} existing customer
                            {dupMatches.length > 1 ? 's' : ''} that match the data you entered.
                            Review below — save anyway only if you are sure.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div
                        className="border border-[#F5F5F5] rounded-md divide-y divide-[#F5F5F5] max-h-60 overflow-y-auto"
                        data-testid="duplicate-matches"
                    >
                        {dupMatches.map((d) => (
                            <div
                                key={d.id}
                                className="px-3 py-2 flex items-center justify-between gap-3"
                            >
                                <div>
                                    <p className="text-sm font-medium text-[#1A1A1A]">
                                        {d.company_name}
                                    </p>
                                    <p className="text-[10px] font-mono text-[#C08478]">
                                        {d.customer_code || '—'}
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-1 justify-end">
                                    {d.matched_fields?.map((f) => (
                                        <Badge
                                            key={f}
                                            className="bg-[#E76F51]/15 text-[#E76F51] text-[10px]"
                                        >
                                            {f}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="duplicate-cancel"
                            onClick={() => {
                                setPendingPayload(null);
                                setDupMatches([]);
                            }}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="duplicate-confirm"
                            onClick={confirmDuplicateSave}
                            disabled={saving}
                            className="bg-[#E76F51] hover:bg-[#d35a3d] text-white"
                        >
                            {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
                            Save Anyway
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

// ---------- small subcomponents ----------

const SectionLabel = ({ children }) => (
    <div className="mt-6 pt-4 border-t border-[#F5F5F5]">
        <p className="text-[10px] uppercase tracking-[2px] text-[#C08478] font-semibold mb-3">
            {children}
        </p>
    </div>
);

const AddressFields = ({ prefix, value, onChange, onPickSuggestion }) => {
    const v = value || {};
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
                <Label>Address Line 1</Label>
                <AddressAutocomplete
                    value={v.line1 || ''}
                    onChange={(line1) => onChange({ line1 })}
                    onSelect={onPickSuggestion}
                    testId={`${prefix}-line1`}
                    placeholder="12 rue de Rivoli, Paris..."
                />
            </div>
            <div className="space-y-2 md:col-span-2">
                <Label>Address Line 2</Label>
                <Input
                    data-testid={`${prefix}-line2`}
                    value={v.line2 || ''}
                    onChange={(e) => onChange({ line2: e.target.value })}
                    placeholder="Apt, suite, building..."
                />
            </div>
            <div className="space-y-2">
                <Label>Postal Code</Label>
                <Input
                    data-testid={`${prefix}-postal-code`}
                    value={v.postal_code || ''}
                    onChange={(e) => onChange({ postal_code: e.target.value })}
                    className="font-mono"
                />
            </div>
            <div className="space-y-2">
                <Label>City</Label>
                <Input
                    data-testid={`${prefix}-city`}
                    value={v.city || ''}
                    onChange={(e) => onChange({ city: e.target.value })}
                />
            </div>
            <div className="space-y-2">
                <Label>State / Region</Label>
                <Input
                    data-testid={`${prefix}-state`}
                    value={v.state || ''}
                    onChange={(e) => onChange({ state: e.target.value })}
                />
            </div>
            <div className="space-y-2">
                <Label>Country</Label>
                <CountrySelect
                    testid={`${prefix}-country`}
                    value={v.country || 'FR'}
                    onChange={(iso) => onChange({ country: iso })}
                    placeholder="Select country"
                    searchPlaceholder="Search country…"
                    emptyLabel="No country found."
                />
            </div>
        </div>
    );
};

export default Customers;
